import React, { useMemo, useState } from 'react';
import styled from 'styled-components';

import { Button, CardActionArea, Typography } from '@material-ui/core';
import { AddCircle, RemoveCircle } from '@material-ui/icons';

import { TraceBoardColumn } from './interfaces/TraceBoardColumn';
import { TraceBoardItem } from './interfaces/TraceBoardItem';
import TraceBoardCard from './TraceBoardCard';
import TraceBoardConnector from './TraceBoardConnector';
import { useTraceBoardContext } from './TraceBoardContext';
import { TraceBoardRowConnector } from './interfaces/TraceBoardRowConnector';

const StyledDiv = styled.div`
  position: relative;
  display: flex;
  margin: 12px;
  border-radius: 4px;
  box-shadow: 0px 1px 1px rgba(0, 0, 0, 0.4);
  overflow: hidden;
  background: #f7f8fc;
  .expand-collapse {
    .button {
      display: flex;
      align-items: flex-start;
      padding: 12px 12px;
      width: 100%;
      height: 100%;
      .collapse {
        color: #fec400;
      }
      .expand {
        color: #29cc97;
      }
    }
  }
  .board-column {
    flex: 1;
    min-width: 320px;
    min-height: 48px;
    background: linear-gradient(90deg, #e4f2fd 0%, #d3e8f4 99.94%);
    padding: 10px 20px 10px 10px;
  }
  .connector-map {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
  }
  .load-more {
    text-align: center;
  }
`;

export interface TraceBoardRowProps {
  rowIndex: number;
  primaryItem?: TraceBoardItem;
  heading?: string | JSX.Element | null;
}

const pageSize = 50; // default number of cards to display per column in row

const TraceBoardRow: React.FC<TraceBoardRowProps> = ({
  rowIndex,
  primaryItem,
  heading
}) => {
  const {
    columns,
    rowCollapse,
    toggleCollapseRow,
    filterColumnItems,
    filterOrphanedItems,
    setSelected
  } = useTraceBoardContext();
  const [limit, setLimit] = useState(pageSize);
  const { columnsFiltered, connectors, hasItems } = useMemo(() => {
    let hasItems = false;
    const connectors: TraceBoardRowConnector[] = [];
    const columnsFiltered = columns.reduce((results, column, index) => {
      if (primaryItem) {
        // get items related to primaryItem for each column
        const previousColumn = results[index - 1];
        hasItems = true;
        results.push({
          ...column,
          items:
            index === 0
              ? // first column is always the primary item
                [primaryItem]
              : // process any linked items from previous column
              previousColumn.items.length
              ? previousColumn.items.reduce((results, sourceItem) => {
                  let items = filterColumnItems(
                    primaryItem,
                    sourceItem,
                    column,
                    index
                  );
                  // identify all connectors for sourceItem in row
                  items.forEach(targetItem => {
                    if (
                      !connectors.some(
                        c =>
                          c.columnIndex === index - 1 &&
                          c.sourceItem.id === sourceItem.id &&
                          c.targetItem.id === targetItem.id
                      )
                    ) {
                      const connector = {
                        columnIndex: index - 1,
                        sourceItem,
                        targetItem
                      };
                      if (!sourceItem.connectors) sourceItem.connectors = [];
                      if (!targetItem.connectors) targetItem.connectors = [];
                      sourceItem.connectors.push(connector);
                      targetItem.connectors.push(connector);
                      connectors.push(connector);
                    }
                  });
                  // prevent duplicate items in a row column
                  // (do this after getting connectors)
                  items = items.filter(
                    (item, index) =>
                      !results.some(i => i.id === item.id) &&
                      items.findIndex(i => i.id === item.id) === index
                  );
                  // store filtered items
                  results.push(...items);
                  return results;
                }, [] as TraceBoardItem[])
              : // if no items in previous column, then try filtering based on primary column
                filterColumnItems(primaryItem, undefined, column, index)
                  .filter(
                    (item, index, items) =>
                      // remove duplicates
                      items.findIndex(i => i.id === item.id) === index
                  )
                  .map(item => {
                    // add connector reference
                    const connector = {
                      columnIndex: index - 1, // faking the index (should be 0)
                      sourceItem: primaryItem,
                      targetItem: item
                    };
                    if (!item.connectors) item.connectors = [];
                    if (!primaryItem.connectors) primaryItem.connectors = [];
                    item.connectors.push(connector);
                    primaryItem.connectors.push(connector);
                    connectors.push(connector);
                    return item;
                  })
        });
      } else if (filterOrphanedItems) {
        // get all orphaned items
        const items = filterOrphanedItems(columns[index], index).filter(
          (item, index, items) =>
            // remove duplicates
            items.findIndex(i => i.id === item.id) === index
        );
        if (!hasItems) hasItems = items.length > 0;
        results.push({ ...column, items });
      }
      return results;
    }, [] as TraceBoardColumn[]);
    return { connectors, columnsFiltered, hasItems };
  }, [columns, primaryItem, filterColumnItems, filterOrphanedItems]);
  const isCollapsed = rowCollapse[rowIndex];
  if (!hasItems) return null;
  return (
    <>
      {heading &&
        (typeof heading === 'string' ? (
          <Typography variant='overline' className='row-heading'>
            {heading}
          </Typography>
        ) : (
          heading
        ))}
      <StyledDiv
        className='board-row'
        onClick={event => {
          const element = event.target as HTMLElement;
          if (element.classList.contains('board-column')) {
            // only unselect if clicking into the empty area of the column
            setSelected(undefined);
          }
        }}
      >
        <svg className='connector-map'>
          {connectors.map(
            ({ columnIndex, sourceItem, targetItem }) =>
              !targetItem.disableTrace && (
                <TraceBoardConnector
                  key={`COLUMN-${columnIndex}->${sourceItem.id}->${targetItem.id}`}
                  rowIndex={rowIndex}
                  columnIndex={columnIndex}
                  sourceItem={sourceItem}
                  targetItem={targetItem}
                />
              )
          )}
        </svg>
        <div className='expand-collapse'>
          <CardActionArea
            className='button'
            onClick={() => toggleCollapseRow(rowIndex)}
          >
            {isCollapsed ? (
              <AddCircle className='expand' />
            ) : (
              <RemoveCircle className='collapse' />
            )}
          </CardActionArea>
        </div>
        {columnsFiltered.map((filteredColumn, index) => (
          <div
            className='board-column'
            key={index}
            // style={{ width: `${100 / columns.length}%` }}
          >
            {filteredColumn.items
              .filter((_, index) => index < limit)
              .map(item => (
                <TraceBoardCard
                  key={item.id}
                  rowIndex={rowIndex}
                  columnIndex={index}
                  item={item}
                  disableLinNewItem={!primaryItem}
                  rowColumns={columnsFiltered}
                />
              ))}
            {limit < filteredColumn.items.length && (
              <div className='load-more'>
                <Button
                  color='primary'
                  onClick={() => setLimit(limit + pageSize)}
                >
                  Show {limit} of {filteredColumn.items.length}
                </Button>
              </div>
            )}
          </div>
        ))}
      </StyledDiv>
    </>
  );
};

export default TraceBoardRow;
